import Vue from 'vue';
import {mapGetters} from 'vuex';
import {resolveAssignmentType} from '@/helpers/baseConfig';

/**
 * Global mixin
 * Anything defined here will be available on every component
 */
Vue.mixin({
   computed: {
      $profile() {
         return this.$store.getters.profile;
      },

      $companyId() {
         return this.$profile.companyId ? this.$profile.companyId : this.$route.params.id;
      },

      $dataLoading() {
         return this.$store.getters.dataLoading;
      },
   },

   methods: {
      $REFACTOR(msg) {
         console.warn(`REFACTOR (${this.$options._componentTag}): ${msg}`);
         return false;
      },

      $clientStaffRoute(route) {
         if (this.$store.getters.isClient) {
            return route;
         }

         let {name, params} = route;
         route.name = `${name}-staff`;
         if (!params) {
            params = {};
         }
         params.id = this.$store.getters['companies/currentCompany'].id;

         return {
            ...route,
            params,
         };
      },

      scrollToTopOfPage() {
         const appRef = this.$store.getters.appComponentRef;
         if (appRef) {
            appRef.scrollTo({top: 0, behavior: 'smooth'});
         }
      },

      toggleDataLoading() {
         const isCurrentlyLoading = this.$dataLoading;
         this.$store.commit('setDataLoading', !isCurrentlyLoading);
      },

      /**
       * Dispatch an action, blocking user input with an overlay until the Promise is resolved
       * @param {string} action - A Vuex action
       * @param {Object} payload - payload object to dispatch the action with
       * @returns The return value from the action
       */
      async blockingRequest(action, payload) {
         let response;
         this.$emit('showSpinner', true);
         try {
            response = await this.$store.dispatch(action, payload);
         } catch (err) {
            console.log(action, err);
            throw err;
         } finally {
            this.$emit('showSpinner', false);
         }
         return response;
      },

      /**
       * Block user input until all the promises are resolved
       * @param {Promise[]} promises - An array of promises
       */
      async blockUntilAllSettled(promises) {
         this.$emit('showSpinner', true);
         return Promise.all(promises).finally(() => {
            this.$emit('showSpinner', false);
         });
      },

      /**
       * Show the pageloading animation until promises are resolved
       * @param {Promise[]} promises - An array of promises
       */
      async loadPage(promises) {
         this.$emit('showPageload', true);
         return Promise.all(promises).finally(() => {
            this.$emit('showPageload', false);
         });
      },
   },
});

const ACCOUNTS_FIXED_COLS = 1;
const VENDORS_FIXED_COLS = 2;
const PERCENTAGE_COL_BY_SECTION = {accounts: 0, vendors: 1};

/**
 * FocusCell Mixin - auto-available to ALL components using v-grid-keynav
 */
export const focusCellMixin = {
   methods: {
      focusCell({section, tableIndex, rowIndex, colIndex, rowStep = 0, colStep = 0}) {
         // The rendered dimension columns are either projects or activities
         // (see showingActivities/dimensionFields) -- for employees, whichever
         // is currently shown can have more columns than the other. Bounding
         // this against this.projects unconditionally silently blocked
         // keyboard nav past whatever the project count happened to be
         // whenever activities (a different count) were the ones on screen.
         const dimensionCols = this.dimensionFields?.length ?? this.projects?.length ?? 0;

         let maxRow;
         let maxCol;
         if (section === 'accounts') {
            if (!this.accountFields?.[tableIndex]) return;
            maxRow = this.accountFields[tableIndex].length - 1;
            maxCol = ACCOUNTS_FIXED_COLS + dimensionCols - 1;
         } else if (section === 'vendors') {
            if (!this.vendorSectionTables?.[tableIndex]) return;
            maxRow = this.vendorSectionTables[tableIndex].length - 1;
            maxCol = VENDORS_FIXED_COLS + dimensionCols - 1;
         } else return;

         const findEnabled = (row, col) => {
            if (row < 0 || row > maxRow || col < 0 || col > maxCol) return null;
            const cellAttr = `[data-grid-section="${section}"][data-grid-table="${tableIndex}"][data-grid-row="${row}"][data-grid-col="${col}"]`;
            const candidate = this.$el.querySelector(`${cellAttr} input, ${cellAttr} textarea`);
            return candidate && !candidate.disabled ? candidate : null;
         };

         this.$nextTick(() => {
            // Disabled inputs (e.g. a payee locked by a 0% allocation) can't
            // receive focus, so keep stepping in the requested direction
            // until an enabled cell is found or the grid edge is reached.
            let el = null;

            if (colStep) {
               let col = colIndex;
               while (col <= maxCol) {
                  el = findEnabled(rowIndex, col);
                  if (el) break;
                  col += colStep;
               }

               // Ran out of columns in this row (e.g. Shift+Enter on the last
               // placeholder) -- drop to the next row's percentage column
               // instead of the first project/activity cell.
               if (!el) {
                  const percentageCol = PERCENTAGE_COL_BY_SECTION[section];
                  let row = rowIndex + 1;
                  while (row <= maxRow) {
                     el = findEnabled(row, percentageCol);
                     if (el) break;
                     row += 1;
                  }
               }
            } else if (rowStep) {
               let row = rowIndex;
               while (row >= 0 && row <= maxRow) {
                  el = findEnabled(row, colIndex);
                  if (el) break;
                  row += rowStep;
               }
            } else {
               el = findEnabled(rowIndex, colIndex);
            }

            if (el?.focus) {
               el.focus();
               el.select?.();
            }
         });
      },
   },
};

/**
 * Adds support for providing suggestions for department, title, and subsidiary when creating client users
 * Assumes that `this.companyId` contains the company ID
 */
export const clientUserSuggestions = {
   computed: {
      ...mapGetters({_suggestionCompany: 'companies/currentCompany'}),

      /** An array of unique job titles for existing employees/users of the selected company */
      companyTitles() {
         if (this._suggestionCompany && this._suggestionCompany.titles) {
            return this._suggestionCompany.titles;
         } else {
            return [];
         }
      },

      /** An array of unique departments for existing employees/users of the selected company */
      companyDepartments() {
         if (this._suggestionCompany && this._suggestionCompany.departments) {
            return this._suggestionCompany.departments;
         } else {
            return [];
         }
      },

      /** An array of unique subsidiary company names of the selected company */
      companySubsidiaries() {
         if (this._suggestionCompany && this._suggestionCompany.subsidiaries) {
            return this._suggestionCompany.subsidiaries;
         } else {
            return [];
         }
      },
   },

   methods: {
      /**
       * Load detailed company data, which includes distinct departments and titles
       * of the users/employees of the company
       * @param {Boolean} [force=false] - force reload
       */
      async loadSuggestions(force = false) {
         if (this.companyId) {
            await this.$store.dispatch('companies/loadCompany', {
               companyId: this.companyId,
               detail: true,
               force,
            });
         }
      },
   },
};

/**
 * Common mixin for gating expense "add" affordances by resource-level permissions.
 * Assumes `this.expenseType` and, for `addManageLabel`, `this.entityLabelPlural` are provided
 * by the host component.
 */
export const expensePermissionsMixin = {
   computed: {
      ...mapGetters({
         isRndig: 'isRndig',
         currentUserPermissionFlags: 'users/currentUserPermissionFlags',
      }),

      hasPayrollAccess() {
         return this.isRndig || this.currentUserPermissionFlags.payroll;
      },

      hasContractorPaymentsAccess() {
         return this.isRndig || this.currentUserPermissionFlags.contractorPayments;
      },

      addManageLabel() {
         const canAdd =
            (this.expenseType === 'contractors' && this.hasContractorPaymentsAccess) ||
            (this.expenseType === 'employees' && this.hasPayrollAccess) ||
            (this.expenseType !== 'contractors' && this.expenseType !== 'employees');
         return `${canAdd ? 'Add/' : ''}Manage ${this.entityLabelPlural}`;
      },
   },
};

/**
 * Common mixin for manager message components
 */

export const managerMessageMixin = {
   computed: {
      ...mapGetters({
         manager: 'manager', //session
         profile: 'profile', //session
         isSME: 'isSME', //session
         isCustomer: 'isCustomer', //session
         activeStudy: 'companies/activeStudy',
         studyPeriodMap: 'companies/studyPeriodMap',
      }),

      isCompleted() {
         return this.$store.getters[`${this.expenseType}/isCompleted`];
      },

      // TODO: Shared expense functionality, introduce expenseHandlers mixin and remove || global mixin
      periodId() {
         return this.$route.params.periodId;
      },

      periodIds() {
         return this.$store.getters[`${this.expenseType}/supportedPeriodIds`];
      },

      sectionAssignments() {
         const assignmentType = resolveAssignmentType(this.expenseType);
         return this.activeStudy?.[assignmentType] || [];
      },

      areAllAssignmentsComplete() {
         return this.sectionAssignments.every(({completed}) => !!completed);
      },
   },

   methods: {
      async unlock() {
         const modalOptions = {
            title: 'Unlock this section?',
            centered: true,
         };

         const warningMsg =
            'Unlocking this section may delay your credit calculation. ' +
            "You will have to mark the section complete again once you're done.";

         const confirmed = await this.$bvModal.msgBoxConfirm(warningMsg, modalOptions);

         if (!confirmed) return;

         try {
            await this.blockingRequest(`${this.expenseType}/unlock`, {companyId: this.$companyId});
         } catch (err) {
            console.error('[unlock] failed to unlock section:', err);
         }
      },
   },
};
